import { makeAutoObservable, runInAction, reaction, type IReactionDisposer } from 'mobx'
import { type IParamsFiltersListGroups } from 'entities/Broadcast/api/types'
import { Segment } from 'entities/Segment/model/Segment'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import { type IResponseFullCampaign } from 'entities/Campaign'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { EnrollmentFilters, EnrollmentStore } from 'widgets/ContactEnrollment'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { type CampaignSettingsStore } from 'pages/campaigns/ui/CampaignSettings/store/CampaignSettingsStore'

export class CampaignEnrollContactsStore {
  private _enrollmentFilters = EnrollmentFilters.Empty()
  private _enrollmentStore: EnrollmentStore
  private _loading = false
  private _number_id: number | null = null
  private _disposeIsShowContactsAlert: IReactionDisposer | null = null

  constructor(
    private _entities: IEntitiesStore,
    private _campaignSettingsStore: CampaignSettingsStore
  ) {
    makeAutoObservable(this)

    this._enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () => {
            return ContactFiltersApi.getFilters({
              workflowSource: 'campaign',
              skipValues: ['tag'],
            }).then(({ data }) => data)
          },
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this._entities
    )

    this.initFilters()
    this.reactionIsShowContactsAlert()

    this._campaignSettingsStore.setOnReEnterChange((value) => {
      this._enrollmentStore.setIsContactReEnroll(value)
      this._enrollmentStore.setContactReEnrollAlert(this._enrollmentFilters.filters)
    })
  }

  get enrollmentFilters() {
    return this._enrollmentFilters
  }

  get enrollmentStore() {
    return this._enrollmentStore
  }

  error = ''
  setError = (value: typeof this.error) => {
    this.error = value
  }

  get loading() {
    return this._loading
  }

  get isEmpty() {
    return !this._enrollmentFilters.allSize
  }

  handleReEnroll = (value: boolean) => {
    this._enrollmentStore.setIsContactReEnroll(value)
    this._campaignSettingsStore.setIsReEnter(value)
  }

  get paramsFilters(): IParamsFiltersListGroups['filtersListGroups'] {
    return {
      contacts: this._enrollmentFilters.contacts.map((contact) => contact.id) ?? [],
      segments: this._enrollmentFilters.segments?.map((segment) => segment.id) ?? [],
      tags: this._enrollmentFilters.tags?.map((tag) => tag.id),
      advanced: this._enrollmentFilters.filters?.map((filter) => filter.filtersParams),
    }
  }

  initFilters = () => {
    this._enrollmentStore.filterEditorStore.initFilters()
  }

  init = async (filtersListGroups: IResponseFullCampaign['filters']) => {
    if (!filtersListGroups) return
    try {
      this._loading = true
      const tagsIds = filtersListGroups.tags || []
      const contactsIds = filtersListGroups.contacts || []
      const segmentsIds = filtersListGroups.segments || []

      const [tags, contacts] = await Promise.all([
        this.fetchTags(tagsIds),
        this._entities.contactsStore.getByIds(contactsIds),
      ])
      const segments = segmentsIds.reduce<Segment[]>((state, id) => {
        const item = this._entities.segmentsStore.getItem(id)

        if (item) {
          state.push(item)
        }

        return state
      }, [])
      const advanced = filtersListGroups.advanced?.map(
        (item) =>
          new Segment({
            id: -1,
            organization_id: 0,
            name: '',
            filters: item,
            is_favorite: false,
          })
      )

      this._enrollmentFilters.sync({
        tags: tags,
        segments: segments,
        contacts: contacts,
        filters: advanced,
        number_id: this._number_id,
      })

      this._enrollmentStore.setIsContactReEnroll(this._campaignSettingsStore.isReEnter)
      this._enrollmentStore.setContactReEnrollAlert(this._enrollmentFilters.filters)
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  fetchTags = async (ids: number[]) => {
    const tags = await TagsApi.getTagsById(ids)

    return tags.map((tag) => new Tag(tag))
  }

  editEnrollment = async () => {
    const filters = this._enrollmentFilters.clone()

    const cancelFilters = () => Promise.resolve(true)

    const confirmFilters = async (newFilters: EnrollmentFilters) => {
      runInAction(() => {
        this._enrollmentFilters = newFilters
      })
      return true
    }

    this._enrollmentStore.openModal({
      filters: filters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
      allowEmptyCount: true,
      title: 'Contacts',
    })
  }

  dispose = () => {
    this._disposeIsShowContactsAlert?.()
  }

  reactionIsShowContactsAlert = () => {
    this._disposeIsShowContactsAlert = reaction(
      () => [this._enrollmentStore.isContactReEnroll, this._enrollmentFilters.filters],
      () => this._enrollmentStore.setContactReEnrollAlert(this._enrollmentFilters.filters)
    )
  }
}
